/**
 * Responsive image URL helper for S3-hosted images.
 * 
 * When images are uploaded, a _w480 variant is created alongside the original.
 * This helper provides the small URL with a fallback mechanism.
 * 
 * Usage:
 *   <img src={getResponsiveSrc(url, 480)} onError={handleImageError} />
 */

const S3_PREFIX = "https://campyid-ver1.s3.us-east-2.amazonaws.com/";

/**
 * Given an S3 image URL, return the _w480 variant URL.
 * Returns null if the URL is not an S3 URL or already a variant.
 */
export function getSmallImageUrl(url) {
  if (!url || !url.startsWith(S3_PREFIX)) return null;
  if (url.includes("_w480.")) return url; // Already a variant
  
  const dotIdx = url.lastIndexOf(".");
  if (dotIdx === -1) return null;
  
  return url.substring(0, dotIdx) + "_w480.webp";
}

/**
 * For small display contexts (thumbnails, cards), try the _w480 variant first.
 * Returns the small URL if it's an S3 image, otherwise the original.
 * MUST be paired with onError fallback to handle missing variants.
 */
export function getResponsiveSrc(url, maxDisplayWidth = 480) {
  if (!url || !url.startsWith(S3_PREFIX)) return url;
  if (maxDisplayWidth > 480) return url; // Only use variant for small displays
  return getSmallImageUrl(url) || url;
}

/**
 * onError handler for responsive images.
 * When the _w480 variant doesn't exist (404), falls back to the original URL.
 * Attach to img: onError={(e) => handleImageError(e, originalUrl)}
 */
export function handleImageError(e, originalUrl) {
  const img = e.currentTarget;
  // Only retry once — if src already equals original, don't loop
  if (img.src !== originalUrl && originalUrl) {
    img.src = originalUrl;
  }
}
